import React, {
  useState, useCallback, useEffect, FunctionComponent, useMemo,
} from 'react';
import {
  Image,
  Modal,
  ModalSize,
  ModalCloseVariant,
  ICON_FOLDER_MULTIPLE_IMAGE,
  ICON_HOME,
  ICON_MAP,
  ICON_MAP_MARKER_RADIUS,
  ICON_VIRTUAL_TOUR,
  ICON_YOUTUBE,

} from '@doveit/lego';
import { toSrcSetData } from '../../../utils/property/image';
import ImageCount from '../../../components/image-count/ImageCount';
import ToolboxNavigation from '../../../components/toolbox-navigation/ToolboxNavigation';
import { GalleryModalContent } from './gallery-modal-content/GalleryModalContent';
import { GallerySectionItem } from './constants';
import * as styles from './GallerySection.style';
import { PropertyImage } from '../../../../services/dtos/property';

export * from './constants';

export interface GallerySectionProps {
  title: string,
  sold?: boolean,
  allowedItems?: GallerySectionItem[],
  images?: PropertyImage[],
  current?: GallerySectionItem,
  virtualTourSrc?: string,
  videoSrc?: string,
  normalizedAddress?: string,
  blueprints?: PropertyImage[],
  coordinates?: string,
}

interface ToolboxItem {
  id: GallerySectionItem,
  icon: string,
  label: (props: GallerySectionProps) => string,
  condition: (props: GallerySectionProps) => boolean,
}

const TOOLBOX_ITEMS: ToolboxItem[] = [
  {
    id: GallerySectionItem.GALLERY,
    icon: ICON_FOLDER_MULTIPLE_IMAGE,
    label: ({ images }) => `Foto (${images.length || 0})`,
    condition: ({ images }) => images && images.length > 0,
  },
  {
    id: GallerySectionItem.VIRTUAL_TOUR,
    icon: ICON_VIRTUAL_TOUR,
    label: () => 'Virtual tour',
    condition: ({ virtualTourSrc }) => !!virtualTourSrc,
  },
  {
    id: GallerySectionItem.VIDEO,
    icon: ICON_YOUTUBE,
    label: () => 'Video',
    condition: ({ videoSrc }) => !!videoSrc,
  },
  {
    id: GallerySectionItem.MAP,
    icon: ICON_MAP,
    label: () => 'Mappa',
    condition: ({ normalizedAddress }) => !!normalizedAddress,
  },
  {
    id: GallerySectionItem.STREET_VIEW,
    icon: ICON_MAP_MARKER_RADIUS,
    label: () => 'Street View',
    condition: ({ coordinates }) => !!coordinates,
  },
  {
    id: GallerySectionItem.BLUEPRINTS,
    icon: ICON_HOME,
    label: ({ blueprints }) => `Planimetria (${blueprints.length || 0})`,
    condition: ({ blueprints }) => blueprints && blueprints.length > 0,
  },
];

export function createToolbox(props: GallerySectionProps) {
  const { allowedItems = Object.values(GallerySectionItem) } = props;

  return TOOLBOX_ITEMS
    .filter((item) => allowedItems.includes(item.id) && item.condition(props))
    .map((item) => ({
      id: item.id,
      icon: item.icon,
      label: item.label(props),
    }));
}

export const GallerySection: FunctionComponent<GallerySectionProps> = (props) => {
  const {
    title,
    images,
    sold,
    current,
  } = props;

  const [currentItem, setCurrentItem] = useState(current);
  const toolboxItems = useMemo(() => createToolbox(props), [props]);

  useEffect(() => setCurrentItem(current), [current]);

  const onCoverImageClick = useCallback(() => {
    setCurrentItem(GallerySectionItem.GALLERY);
  }, [setCurrentItem]);

  const onToolboxSelected = useCallback(({ id }) => {
    setCurrentItem(id);
  }, [setCurrentItem]);

  const onCloseModal = useCallback(() => {
    setCurrentItem(null);
  }, [setCurrentItem]);

  return (
    <div data-ref="gallery-section" aria-label="Sezione galleria immagini">
      {images && images.length && (
        <div
          data-ref="gallery-section-cover-image"
          css={styles.gallery}
          onClick={onCoverImageClick}
          role="presentation"
        >
          <Image
            src={images[0].medium}
            srcSetData={toSrcSetData(images[0])}
            responsive
            alt={title}
          />
          <div css={styles.imageCount}>
            <ImageCount total={images.length} />
          </div>
          {sold && (
            <div
              css={styles.sold}
              data-ref="gallery-thumbnail-sold-banner"
            >
              Venduto
            </div>
          )}
        </div>
      )}
      <div>
        <ToolboxNavigation
          data-ref="gallery-section-toolbox"
          activeItemId={currentItem}
          onSelectedItemChange={onToolboxSelected}
        >
          {toolboxItems.map((item) => (
            <ToolboxNavigation.Item
              id={item.id}
              icon={item.icon}
              label={item.label}
              key={item.id}
            />
          ))}
        </ToolboxNavigation>
      </div>
      <Modal
        open={!!currentItem}
        onCloseHandler={onCloseModal}
        fillAvailableHeight
        size={ModalSize.XLARGE}
      >
        <Modal.Close variant={ModalCloseVariant.OVERLAY} />
        <Modal.Body>
          {currentItem && (
            <GalleryModalContent
              {...props}
              item={currentItem}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <div css={styles.minimalToolboxWrapper}>
            <ToolboxNavigation
              data-ref="gallery-section-modal-toolbox"
              activeItemId={currentItem}
              minimal
              onSelectedItemChange={onToolboxSelected}
            >
              {toolboxItems.map((item) => (
                <ToolboxNavigation.Item
                  id={item.id}
                  icon={item.icon}
                  label={item.label}
                  key={item.id}
                />
              ))}
            </ToolboxNavigation>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GallerySection;
